import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NavigationCallback, NavigationDropdown, NavigationLink, NavigationService } from '@rms/core/navigation';
import { filter } from 'rxjs/operators';

import { dropdownAnimation } from '../../../animations/dropdown.animation';

@Component({
  selector: 'rms-sidenav-item',
  templateUrl: './sidenav-item.component.html',
  styleUrls: ['./sidenav-item.component.scss'],
  animations: [dropdownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatRippleModule, MatTooltipModule, RouterLinkActive, RouterLink, MatIconModule, NgClass, TranslateModule],
})
export class SidenavItemComponent implements OnInit, OnChanges {
  /**
   * Deliberately `any`: the template reads `route`/`children`/`onClick`/`badge` behind the
   * `isLink`/`isDropdown`/`isCallback` guards, but those are instance properties, and Angular's
   * template type-checker does not narrow a union through a property reference.
   */
  @Input({ required: true }) public item!: any;
  @Input({ required: true }) public level!: number;
  @Input() public showTooltip = false;
  @HostBinding('class')
  public get levelClass(): string {
    return `item-level-${this.level}`;
  }

  private readonly router = inject(Router);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly navigationService = inject(NavigationService);
  private readonly destroyRef = inject(DestroyRef);

  public isOpen = false;
  public isActive = false;
  public isHover = false;
  public isLink = this.navigationService.isLink;
  public isExternalLink = this.navigationService.isExternalLink;
  public isDropdown = this.navigationService.isDropdown;
  public isSubheading = this.navigationService.isSubheading;
  public isCallback = this.navigationService.isCallback;

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter(() => this.isDropdown(this.item)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.onRouteChange());

    this.navigationService.openChange$
      .pipe(
        filter(() => this.isDropdown(this.item)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((item) => this.onOpenChange(item));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && this.isDropdown(this.item)) {
      this.onRouteChange();
    }
  }

  public toggleOpen(): void {
    this.isOpen = !this.isOpen;
    this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
    this.cd.markForCheck();
  }

  public onOpenChange(item: NavigationDropdown): void {
    if (this.isChildrenOf(this.item as NavigationDropdown, item)) {
      return;
    }

    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      return;
    }

    if (this.item !== item) {
      this.isOpen = false;
      this.cd.markForCheck();
    }
  }

  public onRouteChange(): void {
    const isActive = this.hasActiveChilds(this.item as NavigationDropdown);

    this.isActive = isActive;
    this.isOpen = isActive;
    this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
    this.cd.markForCheck();
  }

  public isChildrenOf(parent: NavigationDropdown, item: NavigationDropdown): boolean {
    if (parent.children.indexOf(item) !== -1) {
      return true;
    }

    return parent.children
      .filter((child) => this.isDropdown(child))
      .some((child) => this.isChildrenOf(child as NavigationDropdown, item));
  }

  public hasActiveChilds(parent: NavigationDropdown): boolean {
    return parent.children.some((child) => {
      if (this.isDropdown(child)) {
        return this.hasActiveChilds(child as NavigationDropdown);
      }

      if (this.isCallback(child)) {
        return (child as NavigationCallback).isActive ?? false;
      }

      if (this.isLink(child) && !this.isFunction((child as NavigationLink).route)) {
        return this.router.isActive((child as NavigationLink).route as string, false);
      }

      return false;
    });
  }

  public isFunction(prop: NavigationLink['route']): boolean {
    return prop instanceof Function;
  }

  public hoverIcon(): void {
    this.isHover = true;
  }

  public hoverOutIcon(): void {
    this.isHover = false;
  }
}
